import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  getEncounterServiceTrackerList,
  updateEncounterServiceTracker,
} from "apiHandler/actions";
import { getPracticeUsers } from "apiHandler/actions/clinical";
import { getFormattedDateTime, trimmedDate } from "utils/helper";
import {
  getSelectedBulkFields,
  getSelectedBulkType,
  getBulkValueOptions,
  buildBulkTrackerList,
} from "./bulkUpdateHelper";
import { BULK_FIELDS } from "./helper";

const useDictationSummary = ({
  userData,
  token,
  dateOfService,
  npi,
  dos,
  setSnackbar,
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [practiceUsers, setPracticeUsers] = useState([]);
  const [bulkFields, setBulkFields] = useState([]);
  const [bulkValue, setBulkValue] = useState("");

  useEffect(() => {
    if (userData?.selected_practice) {
      getDictationSummary();
      getPractices();
    }
  }, [dos, userData?.selected_practice]);

  const getDictationSummary = async () => {
    setLoading(true);
    setData([]);

    try {
      const payload = {
        practice_id: userData?.selected_practice,
        dos: dateOfService || dayjs(dos).format("MM/DD/YYYY"),
        npi: npi,
      };

      const res = await getEncounterServiceTrackerList(payload, token);

      if (Array.isArray(res)) {
        const uniqueData = [];
        const seen = new Set();

        for (const item of res) {
          if (!seen.has(item.patient_id)) {
            seen.add(item.patient_id);
            uniqueData.push({
              ...item,
              dos: trimmedDate(item.dos),
            });
          }
        }

        setData(uniqueData);
        setSelectedRowIds([]);
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const getPractices = async () => {
    try {
      const payload = { practice_id: `${userData.selected_practice}` };
      const res = await getPracticeUsers(payload, token);

      if (Array.isArray(res)) {
        setPracticeUsers(res.filter((user) => user?.user_role !== "Provider"));
      } else {
        setPracticeUsers([]);
      }
    } catch (error) {
      console.log("error: ", error);
      setPracticeUsers([]);
    }
  };

  const shouldRefreshAfterUpdate = (row) => !row?.tracker_id;

  const getCompletedResourceField = (statusField) => {
    const fieldMap = {
      adv_chart_status: "resource_id_adv_chart",
      scribing_status: "resource_id_scribing",
      coding_status: "resource_id_coding",
    };

    return fieldMap[statusField];
  };

  const selectedBulkFields = useMemo(
    () => getSelectedBulkFields(bulkFields),
    [bulkFields],
  );

  const selectedBulkType = useMemo(
    () => getSelectedBulkType(selectedBulkFields),
    [selectedBulkFields],
  );

  const bulkValueOptions = useMemo(
    () =>
      getBulkValueOptions(selectedBulkFields, selectedBulkType, practiceUsers),
    [selectedBulkFields, selectedBulkType, practiceUsers],
  );

  const handleBulkFieldChange = (values) => {
    if (!values?.length) {
      setBulkFields([]);
      setBulkValue("");
      return;
    }

    const selectedItems = BULK_FIELDS.filter((field) =>
      values.includes(field.value),
    );

    const firstType = selectedItems[0]?.type;

    const filteredValues = selectedItems
      .filter((item) => item.type === firstType)
      .map((item) => item.value);

    if (filteredValues.length !== values.length) {
      setSnackbar({
        open: true,
        message:
          "You can select either multiple status fields or multiple agent fields, not both together.",
        severity: "warning",
      });
    }

    setBulkFields(filteredValues);
    setBulkValue("");
  };

  const handleClearSelection = () => {
    setSelectedRowIds([]);
    setBulkFields([]);
    setBulkValue("");
  };

  const updateSelectedDictations = async (
    id,
    field,
    newValue,
    completionDateField,
    completionDate,
  ) => {
    const rowToUpdate = data.find((r) => r.patient_id === id);
    if (!rowToUpdate) return;

    const updatedRow = {
      ...rowToUpdate,
      [field]: newValue,
      dos: dayjs(rowToUpdate.dos).format("MM/DD/YYYY"),
      last_updated_by: `${userData.first_name} ${userData.last_name}`,
    };

    if (newValue === "Completed" && completionDateField) {
      updatedRow[completionDateField] = completionDate
        ? dayjs(completionDate).format("MM/DD/YYYY")
        : dayjs().format("MM/DD/YYYY");
    } else if (completionDateField) {
      updatedRow[completionDateField] = null;
    }

    try {
      const res = await updateEncounterServiceTracker(
        { tracker_list: [updatedRow] },
        token,
      );

      if (
        res &&
        res.status === "Successfully updated encounter service tracker"
      ) {
        setSnackbar({
          open: true,
          message: "Updated successfully",
          severity: "success",
        });
        if (shouldRefreshAfterUpdate(rowToUpdate)) {
          getDictationSummary();
        }
      }
    } catch (error) {
      console.log("error: ", error);

      setSnackbar({
        open: true,
        message: "Failed to update. Reverted changes.",
        severity: "error",
      });
    }
  };

  const handleBulkUpdate = async () => {
    if (!bulkFields.length || !bulkValue || selectedRowIds.length === 0) return;

    const selectedRows = data.filter((row) =>
      selectedRowIds.includes(row.patient_id),
    );

    if (!selectedRows.length) return;

    const tracker_list = buildBulkTrackerList({
      selectedRows,
      bulkFields,
      bulkValue,
      userData,
    });

    const loggedInUserName = `${userData.first_name} ${userData.last_name}`;

    const updatedTrackerList = tracker_list.map((row) => {
      const updatedRow = { ...row };

      bulkFields.forEach((field) => {
        const resourceField = getCompletedResourceField(field);

        if (resourceField && bulkValue === "Completed") {
          updatedRow[resourceField] = loggedInUserName;
        }
      });

      return updatedRow;
    });

    try {
      setLoading(true);
      const res = await updateEncounterServiceTracker(
        { tracker_list: updatedTrackerList },
        token,
      );

      if (
        res &&
        res.status === "Successfully updated encounter service tracker"
      ) {
        setBulkFields([]);
        setBulkValue("");
        setSnackbar({
          open: true,
          message: "Bulk update applied successfully",
          severity: "success",
        });
      }
    } catch (error) {
      console.log("bulk update error: ", error);
      setSnackbar({
        open: true,
        message: "Failed to apply bulk update",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateErrorFields = async (id, errorExists, errorDetails) => {
    const rowToUpdate = data.find((r) => r.patient_id === id);
    if (!rowToUpdate) return;

    const updatedRow = {
      ...rowToUpdate,
      error_exists: errorExists,
      error_details: JSON.stringify(errorDetails),
      dos: dayjs(rowToUpdate.dos).format("MM/DD/YYYY"),
      last_updated_by: `${userData.first_name} ${userData.last_name}`,
    };

    try {
      const res = await updateEncounterServiceTracker(
        { tracker_list: [updatedRow] },
        token,
      );

      if (
        res &&
        res.status === "Successfully updated encounter service tracker"
      ) {
        setSnackbar({
          open: true,
          message: "Error details updated successfully",
          severity: "success",
        });
        getDictationSummary();
      }
    } catch (error) {
      console.log("error: ", error);
      setSnackbar({
        open: true,
        message: "Failed to update error details. Reverted changes.",
        severity: "error",
      });
    }
  };

  const updateAssignedUser = async (id, field, userId) => {
    const rowToUpdate = data.find((r) => r.patient_id === id);
    if (!rowToUpdate) return;

    const updatedRow = {
      ...rowToUpdate,
      [field]: userId,
      dos: dayjs(rowToUpdate.dos).format("MM/DD/YYYY"),
      last_updated_by: `${userData.first_name} ${userData.last_name}`,
    };

    try {
      const res = await updateEncounterServiceTracker(
        { tracker_list: [updatedRow] },
        token,
      );

      if (
        res &&
        res.status === "Successfully updated encounter service tracker"
      ) {
        setSnackbar({
          open: true,
          message: "Agent updated successfully",
          severity: "success",
        });
        if (shouldRefreshAfterUpdate(rowToUpdate)) {
          getDictationSummary();
        }
      }
    } catch (error) {
      console.log("error: ", error);

      setSnackbar({
        open: true,
        message: "Failed to update agent. Reverted changes.",
        severity: "error",
      });
    }
  };

  const handleIgnoreAll = async () => {
    if (!selectedRowIds.length) return;

    const selectedRows = data.filter((row) =>
      selectedRowIds.includes(row.patient_id),
    );

    const tracker_list = selectedRows.map((row) => ({
      ...row,

      // statuses
      adv_chart_status: "N/A",
      scribing_status: "N/A",
      coding_status: "N/A",

      // agents
      resource_id_adv_chart: "NA",
      resource_id_scribing: "NA",
      resource_id_coding: "NA",

      // reset completion dates
      adv_chart_compl_date: null,
      scribing_compl_date: null,
      coding_compl_date: null,

      dos: dayjs(row.dos).format("MM/DD/YYYY"),
      last_updated_by: `${userData.first_name} ${userData.last_name}`,
      tracker_notes: `Ignored by ${userData.first_name} ${userData.last_name} at ${getFormattedDateTime(new Date())}`,
    }));

    try {
      setLoading(true);

      const res = await updateEncounterServiceTracker({ tracker_list }, token);

      if (
        res &&
        res.status === "Successfully updated encounter service tracker"
      ) {
        setSnackbar({
          open: true,
          message: "Marked as Ignored (N/A) successfully",
          severity: "success",
        });

        setSelectedRowIds([]);
        getDictationSummary();
      }
    } catch (error) {
      console.log("ignore error:", error);

      setSnackbar({
        open: true,
        message: "Failed to apply Ignore",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUndoIgnore = async () => {
    if (!selectedRowIds.length) return;

    const selectedRows = data.filter((row) =>
      selectedRowIds.includes(row.patient_id),
    );

    if (!selectedRows.length) return;

    const tracker_list = selectedRows.map((row) => ({
      ...row,
      adv_chart_status: null,
      scribing_status: null,
      coding_status: null,
      resource_id_adv_chart: null,
      resource_id_scribing: null,
      resource_id_coding: null,
      adv_chart_compl_date: null,
      scribing_compl_date: null,
      coding_compl_date: null,
      dos: dayjs(row.dos).format("MM/DD/YYYY"),
      last_updated_by: `${userData.first_name} ${userData.last_name}`,
      tracker_notes: `Ignore removed by ${userData.first_name} ${userData.last_name} at ${getFormattedDateTime(new Date())}`,
    }));

    try {
      setLoading(true);

      const res = await updateEncounterServiceTracker({ tracker_list }, token);

      if (
        res &&
        res.status === "Successfully updated encounter service tracker"
      ) {
        setSnackbar({
          open: true,
          message: "Ignore removed successfully",
          severity: "success",
        });

        setSelectedRowIds([]);
        getDictationSummary();
      }
    } catch (error) {
      console.log("undo ignore error:", error);
      setSnackbar({
        open: true,
        message: "Failed to undo ignore",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    data,
    setData,
    selectedRowIds,
    setSelectedRowIds,
    practiceUsers,
    bulkFields,
    bulkValue,
    setBulkValue,
    selectedBulkType,
    bulkValueOptions,
    handleBulkFieldChange,
    handleClearSelection,
    handleBulkUpdate,
    getDictationSummary,
    updateSelectedDictations,
    updateAssignedUser,
    updateErrorFields,
    handleIgnoreAll,
    handleUndoIgnore,
  };
};

export default useDictationSummary;
